/**
 * The dashboard adapter for the shared {@link ArtifactCard} — the same shape audiences render as. This file
 * supplies only what is dashboard-specific: the tile icon, the Draft badge, the panel count as the second
 * line, and the two destinations a dashboard has.
 *
 * Dashboards are the one artifact type with TWO targets, which is why `bodyAction` exists on the shared
 * card at all: the whole card opens the in-chat preview panel (a side-by-side view that keeps the
 * conversation), while the CTA leaves for the dashboard's own page. The CTA is omitted until the dashboard
 * is saved — there is no page to go to, and a disabled "Go to dashboard" on a draft is a dead affordance
 * (ui skill rule 10: hide when the control's presence would confuse). Compact keeps both: the card still
 * opens preview, and the CTA still goes to the page when it exists.
 */
import { LayoutDashboard } from 'lucide-react';

import { ArtifactCard } from '@/_shared/components/ArtifactCard';
import { Badge } from '@/_shared/components/ui/badge';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { panelCountLabel } from '@/dashboard/lib/dashboardList';

export interface DashboardArtifactCardProps {
  title: string;
  slug?: string;
  /** Panels in the spec — shown as the second line. The slug is not useful here. */
  panelCount?: number;
  /** Saved dashboards can deep-link to `/d/{slug}`; drafts cannot. */
  saved: boolean;
  /** Only true for unsaved previews — not for saved or not-found dashboards. */
  isDraft?: boolean;
  /** Live preview spec is in the session store, or saved dashboards can hydrate from DB on click. */
  previewAvailable?: boolean;
  onOpen?: () => void;
  /** `compact` is the copy pinned to the top of the transcript — first line only. */
  variant?: 'default' | 'compact';
  className?: string;
}

export function DashboardArtifactCard({
  title,
  slug,
  panelCount,
  saved,
  isDraft = false,
  previewAvailable = false,
  onOpen,
  variant = 'default',
  className,
}: DashboardArtifactCardProps): React.ReactElement {
  const { wp } = useWorkspace();
  const dashboardHref = saved && slug ? wp(`/d/${slug}`) : undefined;
  // One expression, so the narrowing survives to the `bodyAction` spread instead of being re-tested there.
  const openPreview = previewAvailable || saved ? onOpen : undefined;

  return (
    <ArtifactCard
      icon={<LayoutDashboard />}
      title={title}
      badge={
        isDraft ? (
          <Badge variant="warning" bordered className="shrink-0">
            Draft
          </Badge>
        ) : undefined
      }
      meta={panelCount === undefined ? undefined : <span className="truncate">{panelCountLabel(panelCount)}</span>}
      // No page until saved — omit the CTA rather than leaving a disabled "Go to dashboard" on a draft.
      {...(dashboardHref === undefined ? {} : { action: { label: 'Go to dashboard', to: dashboardHref } })}
      {...(openPreview
        ? {
            bodyAction: {
              ariaLabel: `Open dashboard preview: ${title}`,
              hint: 'Open preview',
              onSelect: openPreview,
            },
          }
        : {})}
      variant={variant}
      className={className}
    />
  );
}
